import { type ReactNode } from 'react'
import cx from 'classnames'
import {
  SearchField as AriaSearchField,
  Input as AriaInput,
} from 'react-aria-components'
import { AnimatePresence, m } from 'motion/react'
import { IconButton } from '@/components/IconButton'
import { Icon } from '@/components/Icon'
import styles from './ModalSearchField.module.css'

interface ModalSearchFieldProps {
  /**
   * The current value of the search field.
   */
  value?: string

  /**
   * Called when the search field value changes.
   */
  onChange?: (text: string) => unknown

  /**
   * Placeholder text to show when the search field is empty.
   */
  placeholder?: string

  /**
   * Whether to auto-focus the search field.
   */
  autoFocus?: boolean

  /**
   * Optional label for accessibility.
   */
  'aria-label'?: string

  /**
   * Optional children to render after the search field.
   */
  children?: ReactNode
  /**
   * Test ID attribute for the search input field.
   */
  inputTestId?: string
  /**
   * Test ID attribute for the clear button.
   */
  clearButtonTestId?: string
}

/**
 * A search field component with a clear button.
 */
export function ModalSearchField({
  value,
  onChange,
  placeholder,
  autoFocus,
  'aria-label': ariaLabel,
  inputTestId,
  clearButtonTestId,
  children,
}: ModalSearchFieldProps) {
  return (
    <AriaSearchField
      className={styles.root}
      value={value}
      onChange={onChange}
      onClear={() => onChange?.('')}
      aria-label={ariaLabel ?? placeholder}
    >
      <m.div className={styles.leftIconContainer}>
        <Icon variant="inherit" sm name="search" />
      </m.div>

      <AriaInput
        autoFocus={autoFocus}
        placeholder={placeholder}
        data-test={inputTestId}
        className={({ isFocused, isFocusVisible }) =>
          cx(styles.input, (isFocused || isFocusVisible) && styles.focused)
        }
      />

      <AnimatePresence>
        {value && (
          <m.div
            className={styles.clearContainer}
            transition={{
              duration: 0.15,
            }}
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
          >
            <IconButton
              variant="no-hover"
              label="Clear search"
              name="close"
              testId={clearButtonTestId}
              sm
            />
          </m.div>
        )}
      </AnimatePresence>

      {children}
    </AriaSearchField>
  )
}
